import './styles/styles.scss';

import {
  AttachFile,
  Cancel,
  Delete,
  Edit,
  ExitToApp,
  Mail,
  Newspaper,
  PersonRemove,
  Sync,
  ContentCopy
} from '@mui/icons-material';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import DoDisturbOnIcon from '@mui/icons-material/DoDisturbOn';
import DownloadIcon from '@mui/icons-material/Download';
import FolderIcon from '@mui/icons-material/Folder';
import {
  Button,
  Fab,
  Icon,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip
} from '@mui/material';
import { Attachment, Group, ResolvedEvent } from '@thro-types/events';

import { MouseEvent, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';

import ContextMenu from 'app/components/ContextMenu';
import { handleDownload } from 'app/components/Util/downloadAttachement';
import { translations } from 'locales/i18n';

import { isEnrollable, isEventActive, isRegistrationPeriodOver } from '../helper';

interface OrganizerProps {
  handleEditClick: (e: MouseEvent) => void;
  handleDeleteClick: (e: MouseEvent) => void;
  handleCancelClick: (e: MouseEvent) => void;
  handleCopyClick: (e: MouseEvent) => void;
  hasEnrollments: boolean;
  isActive: boolean;
  hasArticle?: boolean;
  attachments?: Attachment[];
  handleEventExportClick?: (exportSignatureList: boolean) => void;
  handleMailClick?: (
    type: 'PARTICIPATING' | 'PRECALC_PARTICIPATING' | 'WAITING' | 'PRECALC_WAITING'
  ) => void;
  handleArticleClick?: (e: MouseEvent) => void;
  refetch?: () => void;
  isPreCalc?: boolean;
}

interface ParticipantProps {
  event: ResolvedEvent;
  group?: Group;
  handleEnroll: (e: MouseEvent) => void;
  handleUnenroll: (e: MouseEvent) => void;
}
interface GroupCardProps {
  event: ResolvedEvent;
  group: Group;
  handleEnroll: (e: MouseEvent) => void;
  handleUnenroll: (e: MouseEvent) => void;
  handleCancelGroup: (selectedGroup: any) => void;
}

export const OrganizerMenuActions = ({
  handleEditClick,
  handleDeleteClick,
  handleCancelClick,
  handleCopyClick,
  hasEnrollments
}: OrganizerProps) => {
  const { t } = useTranslation();
  return (
    <>
      <MenuItem onClick={e => handleEditClick(e)}>
        <ListItemIcon>
          <Edit sx={{ color: 'lightblue' }} />
        </ListItemIcon>
        <ListItemText>{t(translations.common.edit())}</ListItemText>
      </MenuItem>
      <MenuItem onClick={e => handleCopyClick(e)}>
        <ListItemIcon>
          <ContentCopy sx={{ color: 'lightblue' }} />
        </ListItemIcon>
        <ListItemText>{t(translations.common.copy())}</ListItemText>
      </MenuItem>
      {!hasEnrollments && (
        <MenuItem onClick={e => handleDeleteClick(e)}>
          <ListItemIcon>
            <Delete sx={{ color: 'red' }} />
          </ListItemIcon>
          <ListItemText>{t(translations.common.delete())}</ListItemText>
        </MenuItem>
      )}
      {hasEnrollments && (
        <MenuItem onClick={e => handleCancelClick(e)}>
          <ListItemIcon>
            <Cancel sx={{ color: 'gray' }} />
          </ListItemIcon>
          <ListItemText>{t(translations.common.cancelEvent())}</ListItemText>
        </MenuItem>
      )}
    </>
  );
};

const isUnenrollButtonForGroupWaitingListDisabled = (
  event: ResolvedEvent,
  group: Group | undefined,
  isOrganizer = false
) => {
  const enableUnenrollButton =
    !!group?.enrollments?.find(enrollment => enrollment.id === event.enrollmentId) ||
    !!group?.waitingList?.enrollments?.find(enrollment => enrollment.id === event.enrollmentId);
  return enableUnenrollButton && (isOrganizer || isUnenrollPossible(event));
};

const isUnenrollPossible = (event: ResolvedEvent) => {
  return event.isUnenrollAfterRegPeriodPossible
    ? new Date() < new Date(event.startDate)
    : new Date() < new Date(event.registrationPeriodEnd);
};

export const ParticipantGroupCardActions = ({
  event,
  group,
  handleEnroll,
  handleUnenroll
}: ParticipantProps) => {
  const { t } = useTranslation();
  return (
    <>
      {!event.enrollmentId
        ? !isRegistrationPeriodOver(event) && (
            <Tooltip
              title={
                isEnrollable(event) ? '' : t(translations.pages.events.tooltips.enrollTooEarly())
              }
            >
              <span>
                <Button
                  onClick={e => handleEnroll(e)}
                  disabled={!isEnrollable(event)}
                  variant="contained"
                  style={{ color: 'white' }}
                >
                  {t(translations.common.register())}
                </Button>
              </span>
            </Tooltip>
          )
        : isUnenrollButtonForGroupWaitingListDisabled(event, group) && (
            <Button
              onClick={e => handleUnenroll(e)}
              variant="contained"
              disabled={!isEnrollable}
              style={{ backgroundColor: 'red', color: 'white' }}
            >
              {t(translations.common.unregister())}
            </Button>
          )}
    </>
  );
};

export const OrganizerGroupCardActions = ({
  event,
  group,
  handleEnroll,
  handleUnenroll,
  handleCancelGroup
}: GroupCardProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const enableEnrollment = event.waitingType !== 'EVENT' && isEventActive(event);

  return (
    <div className="cardHeaderButton">
      <Fab
        onClick={e => {
          setOpen(true);
          e.stopPropagation();
          e.preventDefault();
        }}
        ref={anchorRef}
        size="small"
        className="cardHeaderButton"
      >
        <Icon
          className="fas fa-ellipsis-h fa-lg"
          style={{
            width: '1em',
            color: 'black'
          }}
        />
      </Fab>
      <ContextMenu open={open} setOpen={setOpen} anchorEl={anchorRef.current}>
        {enableEnrollment &&
          (!event.enrollmentId ? (
            <MenuItem
              onClick={e => handleEnroll(e)}
              disabled={!isEnrollable(event) || isRegistrationPeriodOver(event)}
            >
              <ListItemIcon>
                <ExitToApp sx={{ color: 'lightblue' }} />
              </ListItemIcon>
              <ListItemText>
                {isEnrollable(event)
                  ? t(translations.common.register())
                  : t(translations.pages.events.tooltips.enrollTooEarly())}
              </ListItemText>
            </MenuItem>
          ) : (
            <MenuItem
              onClick={e => handleUnenroll(e)}
              disabled={!isUnenrollButtonForGroupWaitingListDisabled(event, group, true)}
            >
              <ListItemIcon>
                <PersonRemove sx={{ color: 'red' }} />
              </ListItemIcon>
              <ListItemText>{t(translations.common.unregister())}</ListItemText>
            </MenuItem>
          ))}
        <MenuItem
          onClick={() => {
            handleCancelGroup(group);
          }}
        >
          <ListItemIcon>
            <DoDisturbOnIcon sx={{ color: '#e8ca23' }} />
          </ListItemIcon>
          <ListItemText>{'Gruppe absagen'}</ListItemText>
        </MenuItem>
      </ContextMenu>
    </div>
  );
};

export const OrganizerFabActions = ({
  handleMailClick,
  handleEditClick,
  handleDeleteClick,
  handleEventExportClick,
  handleCancelClick,
  handleArticleClick,
  handleCopyClick,
  hasEnrollments,
  attachments,
  isActive,
  refetch,
  hasArticle,
  isPreCalc
}: OrganizerProps) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRef, setMenuRef] = useState<'export' | 'attachment' | 'mail' | null>(null);
  const menuOpen = Boolean(anchorEl);

  const handleSettingsMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    event.stopPropagation();
    event.preventDefault();
  };
  const handleSettingsMenuClose = (event: React.MouseEvent) => {
    setAnchorEl(null);
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <>
      <Tooltip title={t(translations.pages.events.tooltips.refresh())}>
        <Fab
          onClick={() => {
            refetch && refetch();
          }}
          size="small"
          sx={theme => ({ marginX: theme.spacing(1) })}
        >
          <Sync color="success" />
        </Fab>
      </Tooltip>
      {!!attachments?.length && (
        <Tooltip title={t(translations.pages.events.tooltips.attachments())}>
          <Fab
            onClick={e => {
              handleSettingsMenuClick(e);
              setMenuRef('attachment');
            }}
            size="small"
            sx={theme => ({ marginX: theme.spacing(1) })}
          >
            <AttachFile color="success" />
          </Fab>
        </Tooltip>
      )}
      <Tooltip title={t(translations.pages.events.tooltips.exports())}>
        <Fab
          onClick={e => {
            handleSettingsMenuClick(e);
            setMenuRef('export');
          }}
          size="small"
          sx={theme => ({ marginX: theme.spacing(1) })}
        >
          <DownloadIcon color="success" />
        </Fab>
      </Tooltip>
      <Tooltip title={t(translations.pages.events.tooltips.mail())}>
        <Fab
          onClick={e => {
            handleSettingsMenuClick(e);
            setMenuRef('mail');
          }}
          size="small"
          sx={theme => ({ marginX: theme.spacing(1) })}
          style={{ color: '#FFCC00' }}
        >
          <Mail />
        </Fab>
      </Tooltip>
      {(isActive || hasArticle) && (
        <Tooltip title={t(translations.pages.events.tooltips.createEventArticle())}>
          <Fab
            onClick={handleArticleClick}
            size="small"
            sx={theme => ({ marginX: theme.spacing(1) })}
            style={{ color: '#FFCC00' }}
          >
            <Newspaper />
          </Fab>
        </Tooltip>
      )}
      {isActive && (
        <>
          <Tooltip title={t(translations.pages.events.tooltips.edit())}>
            <Fab
              onClick={handleEditClick}
              size="small"
              sx={theme => ({ marginX: theme.spacing(1) })}
              style={{ color: 'lightskyblue' }}
            >
              <Edit />
            </Fab>
          </Tooltip>
          <Tooltip title={t(translations.pages.events.tooltips.copy())}>
            <Fab
              onClick={handleCopyClick}
              size="small"
              sx={theme => ({ marginX: theme.spacing(1) })}
              style={{ color: 'lightskyblue' }}
            >
              <ContentCopy />
            </Fab>
          </Tooltip>

          {!hasEnrollments && (
            <>
              <Tooltip title={t(translations.pages.events.tooltips.delete())}>
                <Fab
                  onClick={handleDeleteClick}
                  size="small"
                  sx={theme => ({ marginX: theme.spacing(1) })}
                >
                  <Delete color="error" />
                </Fab>
              </Tooltip>
            </>
          )}
          {hasEnrollments && (
            <Tooltip title={t(translations.pages.events.tooltips.cancel())}>
              <Fab
                onClick={handleCancelClick}
                size="small"
                sx={theme => ({ marginX: theme.spacing(1) })}
              >
                <Cancel sx={{ color: 'gray' }} />
              </Fab>
            </Tooltip>
          )}
        </>
      )}
      <Menu
        open={menuOpen && menuRef === 'export'}
        anchorEl={anchorEl}
        onClose={handleSettingsMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
      >
        <MenuItem onClick={() => !!handleEventExportClick && handleEventExportClick(false)}>
          <ListItemIcon>
            <FolderIcon color="success" />
          </ListItemIcon>
          <ListItemText>
            {t(translations.pages.events.eventDetailPage.export.exportEvent())}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={() => !!handleEventExportClick && handleEventExportClick(true)}>
          <ListItemIcon>
            <ContentPasteIcon color="success" />
          </ListItemIcon>
          <ListItemText>
            {t(translations.pages.events.eventDetailPage.export.exportSignature())}
          </ListItemText>
        </MenuItem>
      </Menu>
      <Menu
        open={menuOpen && menuRef === 'attachment'}
        anchorEl={anchorEl}
        onClose={handleSettingsMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
      >
        {attachments?.map(a => (
          <MenuItem
            onClick={() => handleDownload(a.name, `events/attachments/${a.id}/download/`)}
            key={a.id}
          >
            <ListItemIcon>
              <AttachFile color="success" />
            </ListItemIcon>
            <ListItemText>{a.name}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
      <Menu
        open={menuOpen && menuRef === 'mail'}
        anchorEl={anchorEl}
        onClose={handleSettingsMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
      >
        {isPreCalc
          ? [
              <MenuItem
                onClick={() => !!handleMailClick && handleMailClick('PRECALC_PARTICIPATING')}
                key={'PRECALC_PARTICIPATING'}
              >
                <ListItemIcon>
                  <Mail color="success" />
                </ListItemIcon>
                <ListItemText>
                  {t(translations.pages.events.eventDetailPage.mail.toPreCalcParticipants())}
                </ListItemText>
              </MenuItem>,
              <MenuItem
                onClick={() => !!handleMailClick && handleMailClick('PRECALC_WAITING')}
                key={'PRECALC_WAITING'}
              >
                <ListItemIcon>
                  <Mail color="success" />
                </ListItemIcon>
                <ListItemText>
                  {t(translations.pages.events.eventDetailPage.mail.toPreCalcWaitingList())}
                </ListItemText>
              </MenuItem>
            ]
          : [
              <MenuItem
                onClick={() => !!handleMailClick && handleMailClick('PARTICIPATING')}
                key={'PARTICIPATING'}
              >
                <ListItemIcon>
                  <Mail color="success" />
                </ListItemIcon>
                <ListItemText>
                  {t(translations.pages.events.eventDetailPage.mail.toParticipants())}
                </ListItemText>
              </MenuItem>,
              <MenuItem
                onClick={() => !!handleMailClick && handleMailClick('WAITING')}
                key={'WAITING'}
              >
                <ListItemIcon>
                  <Mail color="success" />
                </ListItemIcon>
                <ListItemText>
                  {t(translations.pages.events.eventDetailPage.mail.toWaitingList())}
                </ListItemText>
              </MenuItem>
            ]}
      </Menu>
    </>
  );
};

export const ParticipantFabActions = ({
  event,
  handleEnroll,
  handleUnenroll
}: ParticipantProps) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRef, setMenuRef] = useState<'attachment' | null>(null);
  const menuOpen = Boolean(anchorEl);
  const handleSettingsMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    event.stopPropagation();
    event.preventDefault();
  };
  const handleSettingsMenuClose = (event: React.MouseEvent) => {
    setAnchorEl(null);
    event.preventDefault();
    event.stopPropagation();
  };
  return (
    <>
      {!!event.attachments?.length && !isMobile && (
        <Tooltip title={t(translations.pages.events.tooltips.attachments())}>
          <Fab
            onClick={e => {
              handleSettingsMenuClick(e);
              setMenuRef('attachment');
            }}
            size="small"
            sx={theme => ({ marginX: theme.spacing(1) })}
          >
            <AttachFile color="success" />
          </Fab>
        </Tooltip>
      )}
      {event.waitingType === 'EVENT' &&
        (!event.enrollmentId ? (
          <Tooltip
            title={
              isEnrollable(event)
                ? t(translations.pages.events.tooltips.enroll())
                : t(translations.pages.events.tooltips.enrollTooEarly())
            }
          >
            <span>
              <Button
                onClick={e => handleEnroll(e)}
                disabled={!isEnrollable(event)}
                variant="contained"
                style={{ color: 'white', marginBottom: 6 }}
              >
                {t(translations.common.register())}
              </Button>
            </span>
          </Tooltip>
        ) : (
          isUnenrollPossible(event) && (
            <Tooltip title={t(translations.pages.events.tooltips.unenroll())}>
              <Button
                onClick={e => handleUnenroll(e)}
                variant="contained"
                style={{ backgroundColor: 'red', color: 'white', marginBottom: 6 }}
              >
                {t(translations.common.unregister())}
              </Button>
            </Tooltip>
          )
        ))}
      <Menu
        open={menuOpen && menuRef === 'attachment'}
        anchorEl={anchorEl}
        onClose={handleSettingsMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
      >
        {event.attachments?.map(a => (
          <MenuItem
            onClick={() => handleDownload(a.name, `events/attachments/${a.id}/download/`)}
            key={a.id}
          >
            <ListItemIcon>
              <AttachFile color="success" />
            </ListItemIcon>
            <ListItemText>{a.name}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
